import type * as T from "@src/types";

import { binarySearch } from "../datafeed/helpers";
import * as shapes from "./shapes";

import type * as TV from "@charting_library";

/**
 * Generate standard plot style configuration from indicator style
 */
const getPlotStyleConfig = (
  indicatorStyle: T.BaseIndicator["style"],
  options?: { isBand?: boolean },
) => {
  // Default transparency: bands should be semi-transparent, lines moderately visible
  const DEFAULT_BAND_TRANSPARENCY = 85;
  const DEFAULT_LINE_TRANSPARENCY = 40;
  const defaultTransparency = options?.isBand
    ? DEFAULT_BAND_TRANSPARENCY
    : DEFAULT_LINE_TRANSPARENCY;

  const transparency = indicatorStyle.transparency ?? defaultTransparency;

  return {
    linestyle: shapes.getTvEnumValue("linestyle", indicatorStyle.linestyle),
    linewidth: indicatorStyle.linewidth,
    plottype: shapes.getTvEnumValue("plotype", indicatorStyle.plotype),
    trackPrice: false,
    transparency,
    visible: true,
    color: indicatorStyle.color,
  };
};

/**
 * Generate base metainfo fields for custom indicators
 */
const getBaseMetainfo = (
  name: string,
  position: "chart" | "pane",
  idx: number,
  suffix: string = "",
) => ({
  id: `${name}${suffix}@tv-${position === "pane" ? "panestudies" : "basicstudies"}-${idx}` as T.TV.RawStudyMetaInfoId,
  description: `${name}${suffix}`,
  shortDescription: name,
  format: { type: "inherit" as const },
  linkedToSeries: position === "chart",
  is_price_study: position === "chart",
});

/**
 * Parse indicator name to extract base name and position (upper/lower)
 */
export const parseBandName = (
  name: string,
): { baseName: string; position: "upper" | "lower" } | null => {
  const patterns = [
    // "Stable Upper", "Stable Lower"
    { regex: /^(.+?)\s+(upper|top|high)$/i, baseIndex: 1, posIndex: 2 },
    { regex: /^(.+?)\s+(lower|bottom|low)$/i, baseIndex: 1, posIndex: 2 },
    // "Upper Stable", "Lower Stable"
    { regex: /^(upper|top|high)\s+(.+?)$/i, baseIndex: 2, posIndex: 1 },
    { regex: /^(lower|bottom|low)\s+(.+?)$/i, baseIndex: 2, posIndex: 1 },
    // "Stable_Upper", "Stable_Lower" (underscore/dash)
    { regex: /^(.+?)[-_](upper|top|high)$/i, baseIndex: 1, posIndex: 2 },
    { regex: /^(.+?)[-_](lower|bottom|low)$/i, baseIndex: 1, posIndex: 2 },
  ];

  for (const pattern of patterns) {
    const match = name.match(pattern.regex);
    if (match) {
      const baseName = match[pattern.baseIndex].trim();
      const posStr = match[pattern.posIndex].toLowerCase();
      const position = ["upper", "top", "high"].includes(posStr) ? "upper" : "lower";
      return { baseName, position };
    }
  }

  return null;
};

/**
 * Detect band pairs from indicators array.
 * Creates filled area bands for any indicator pair (upper + lower with same base name).
 * Single indicators without a counterpart are rendered as separate lines.
 */
export const detectBandPairs = (indicators: T.TypedArrayLineIndicator[]): T.BandPair[] => {
  const parsed = indicators
    .map((ind) => ({ indicator: ind, parsed: parseBandName(ind.name) }))
    .filter((item) => item.parsed !== null);

  // Group by base name
  const groups: Record<
    string,
    { upper?: T.TypedArrayLineIndicator; lower?: T.TypedArrayLineIndicator }
  > = {};

  parsed.forEach(({ indicator, parsed }) => {
    if (!parsed) return;
    const key = parsed.baseName.toLowerCase();
    if (!groups[key]) groups[key] = {};
    groups[key][parsed.position] = indicator;
  });

  // Return only complete pairs (both upper and lower exist)
  // Sparse indicators (like TP/SL levels) don't have upper/lower pairs,
  // so they won't match here and will be rendered as individual lines
  return Object.entries(groups)
    .filter(([_, pair]) => pair.upper && pair.lower)
    .map(([baseName, pair]) => ({
      baseName,
      upperIndicator: pair.upper!,
      lowerIndicator: pair.lower!,
      color: pair.upper!.style.color || pair.lower!.style.color,
      position: pair.upper!.position, // Use position from upper (should match lower)
    }));
};

/**
 * Helper: Get indicator value at specific time
 *
 * The whole series is searched every time. An earlier version started the search at the datafeed's
 * last candle index, which is an index into the CANDLES and means nothing here: a series shorter
 * than that index put the search past its own end, so every lookup fell out of bounds and the
 * indicator vanished. A stop that only moves twice is exactly that short. Even a start index that
 * happens to fit is wrong, because it hides everything newer than itself. The saving was a couple
 * of steps on an array of tens of points.
 */
export const getIndicatorValueAtTime = (
  data: T.TypedArrayTimesValues,
  timeInSeconds: number,
): number => {
  if (!data || !data.times || data.times.length === 0) {
    return NaN; // Return NaN to hide point
  }

  const firstTime = data.times[data.times.length - 1];
  const lastTime = data.times[0];

  // Don't render outside actual time boundaries
  if (timeInSeconds < firstTime || timeInSeconds > lastTime) {
    return NaN;
  }

  const index = binarySearch(data.times, timeInSeconds);

  if (index < 0 || index >= data.values.length) {
    return NaN;
  }

  // Return value directly - NaN values in the array will create gaps in the line
  return data.values[index];
};

/**
 * Generate a band group indicator with upper/lower plots and filled area
 */
export const generateBandGroupIndicator = (
  PineJS: T.TV.PineJS,
  pair: T.BandPair,
  idx: number,
): T.TV.CustomIndicator => {
  const upperPlotId = `${pair.baseName}_upper`;
  const lowerPlotId = `${pair.baseName}_lower`;
  const areaId = `${pair.baseName}_area`;

  // Band indicators: hide individual lines, show only the filled area between them.
  // Lines are redundant noise when the area already shows the range.
  // Entry bands (inner, stable) are dimmer — just context. Exit bands (outer) slightly more visible.
  const lineTransparency = 100; // Fully hidden — area conveys the information
  const isEntryBand = pair.baseName.startsWith("entry");
  const areaTransparency = isEntryBand ? 88 : 88;

  return {
    name: `${pair.baseName} Bands`,
    metainfo: {
      ...getBaseMetainfo(pair.baseName, pair.position, idx, " Bands"),

      // Define TWO plots: upper and lower
      plots: [
        {
          id: upperPlotId,
          type: "line" as TV.StudyPlotType.Line,
        },
        {
          id: lowerPlotId,
          type: "line" as TV.StudyPlotType.Line,
        },
      ],

      // Define filled area between the two plots
      filledAreas: [
        {
          id: areaId,
          objAId: upperPlotId,
          objBId: lowerPlotId,
          type: "plot_plot" as TV.FilledAreaType.TypePlots,
          title: `${pair.baseName} Area`,
          fillgaps: true,
          zorder: -1, // Render behind other elements
        },
      ],

      defaults: {
        // Styles for upper/lower band lines — fully hidden (area conveys the range)
        styles: {
          [upperPlotId]: {
            ...getPlotStyleConfig(pair.upperIndicator.style, { isBand: true }),
            transparency: lineTransparency,
          },
          [lowerPlotId]: {
            ...getPlotStyleConfig(pair.lowerIndicator.style, { isBand: true }),
            transparency: lineTransparency,
          },
        },
        // Style for filled area - make it slightly MORE transparent than the lines
        // so the lines are more visible/stronger than the area fill
        filledAreasStyle: {
          [areaId]: {
            color: pair.color,
            transparency: areaTransparency,
            visible: true,
          },
        },
      },
      inputs: [],
    },

    constructor: function () {
      this.init = function (context: any, input: any) {
        this._context = context;
        this._input = input;
      };

      this.main = function (ctx: any, inputCallback: any) {
        this._context = ctx;
        this._input = inputCallback;

        const time = PineJS.Std.time(this._context);
        const timeInSeconds = Math.floor(time / 1000);

        // Get values for both upper and lower bands. An edge marked source:"close" takes the
        // bar's own close instead of its data, which is what lets a band be drawn between a level
        // and the price. Its data still decides WHERE it is drawn, so the band stays bounded to
        // the span the caller sent and does not run to the edge of the chart.
        const edgeValue = (edge: T.TypedArrayLineIndicator) => {
          const value = getIndicatorValueAtTime(edge.data, timeInSeconds);
          if (edge.source !== "close" || Number.isNaN(value)) return value;
          return PineJS.Std.close(this._context);
        };
        const upperValue = edgeValue(pair.upperIndicator);
        const lowerValue = edgeValue(pair.lowerIndicator);

        // Return array with values for BOTH plots
        // Order must match plots array: [upper, lower]
        return [
          { value: upperValue, offset: 0 },
          { value: lowerValue, offset: 0 },
        ];
      };
    },
  };
};

/** A series the chart definition flags as a zone is drawn as a background tint rather than a line. */
export const isZoneIndicator = (indicator: Pick<T.BaseIndicator, "zone">) =>
  indicator.zone === true;

const ZONE_OPACITY = 0.03;

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
const FALLBACK_COLOR = "#808080";

/** A "#rrggbb" color as it is; any other string is grey. */
const hexColorOrGrey = (color: string) => (HEX_COLOR.test(color) ? color : FALLBACK_COLOR);

/** A "#rrggbb" color as rgba() at the given alpha; any other string is drawn in grey. */
export const hexToRgba = (hex: string, alpha: number) => {
  const value = parseInt(hexColorOrGrey(hex).slice(1), 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
};

// A tint has no value of its own, so a legend-only plot prints a square of its color beside the name:
// filled while the zone is active under the cursor, hollow otherwise. The legend plot carries this format,
// and the widget's study formatter factory prints a value that comes with it as the square.
export const ZONE_LEGEND_FORMAT = { type: "percent" as const, precision: 9 };
export const formatZoneLegendValue = (value: number) => (value > 0 ? "■" : "□");

/**
 * Generate a full-height background tint, in the series' color or grey when that is not "#rrggbb", for the
 * candles where its value is positive
 */
export const generateZoneIndicator = (
  PineJS: T.TV.PineJS,
  i: T.TypedArrayLineIndicator,
  idx: number,
): T.TV.CustomIndicator => ({
  name: i.name,
  metainfo: {
    ...getBaseMetainfo(i.name, i.position, idx),
    isCustomIndicator: true,
    plots: [
      { id: "zone", type: "bg_colorer" as TV.StudyPlotType.BgColorer, palette: "zone_palette" },
      { id: "legend", type: "line" as TV.StudyPlotType.Line },
    ],
    palettes: {
      zone_palette: {
        colors: [{ name: i.name }],
        valToIndex: { 0: 0 },
      },
    },
    defaults: {
      palettes: {
        zone_palette: {
          colors: [{ color: hexToRgba(i.style.color, ZONE_OPACITY), width: 1, style: 0 }],
        },
      },
      styles: {
        legend: {
          color: hexColorOrGrey(i.style.color),
          display: 8 as TV.StudyPlotDisplayTarget.StatusLine, // legend only, never drawn or scaled
          linestyle: 0,
          linewidth: 1,
          plottype: 0 as TV.LineStudyPlotStyle.Line,
          trackPrice: false,
          transparency: 0,
          visible: true,
        },
      },
    },
    styles: { legend: { title: i.name, format: ZONE_LEGEND_FORMAT } },
    inputs: [],
  },
  constructor: function () {
    this.main = function (ctx: any) {
      const timeInSeconds = Math.floor(PineJS.Std.time(ctx) / 1000);
      // A dense series stores its leading gap as 0, so only a positive value means the zone is on
      const active = getIndicatorValueAtTime(i.data, timeInSeconds) > 0;
      return [active ? 0 : NaN, active ? 1 : 0];
    };
  },
});

/**
 * Check if indicator data contains NaN values (sparse data with gaps between positions).
 * Scans a sample of the data to detect sparseness efficiently.
 */
const isSparseIndicator = (data: T.TypedArrayTimesValues): boolean => {
  if (!data?.values || data.values.length === 0) return false;
  const sampleSize = Math.min(data.values.length, 200);
  for (let j = 0; j < sampleSize; j++) {
    if (Number.isNaN(data.values[j])) return true;
  }
  return false;
};

/**
 * Generate a single line indicator
 */
export const generateLineIndicator = (
  PineJS: T.TV.PineJS,
  i: T.TypedArrayLineIndicator,
  idx: number,
  options?: { isEditable?: boolean },
): T.TV.CustomIndicator => {
  const isEditable = options?.isEditable ?? false;

  // Sparse indicators (exit levels with NaN gaps between positions) need "linewithbreaks"
  // to avoid drawing dotted lines across gaps. Override for old backtests that have "line".
  const styleConfig = getPlotStyleConfig(i.style);
  if (i.style.plotype === "line" && "values" in i.data && isSparseIndicator(i.data)) {
    styleConfig.plottype = shapes.getTvEnumValue("plotype", "linewithbreaks");
  }

  return {
    name: i.name,
    metainfo: {
      ...getBaseMetainfo(i.name, i.position, idx),
      isCustomIndicator: true,
      plots: [
        {
          id: "plot_0",
          type: "line" as TV.StudyPlotType.Line,
        },
      ],
      defaults: {
        styles: {
          plot_0: styleConfig,
        },
      },
      styles: {
        plot_0: {
          title: i.name,
          histogramBase: 0,
          isHidden: !isEditable,
        },
      },
      inputs: isEditable
        ? [
            {
              id: "length",
              name: "Length",
              defval: 1,
              type: "integer" as TV.StudyInputType.Integer,
            },
          ]
        : [],
    },
    constructor: function () {
      this.init = function (context: any, input: any) {
        this._context = context;
        this._input = input;
      };

      this.main = function (ctx: any, inputCallback: any) {
        this._context = ctx;
        this._input = inputCallback;

        const time = PineJS.Std.time(this._context);
        const timeInSeconds = Math.floor(time / 1000);

        // Check if indicator data exists
        if (!i.data || !i.data.times || i.data.times.length === 0) {
          return [{ value: 0, offset: 0 }];
        }

        const index = binarySearch(i.data.times, timeInSeconds);

        // if type is candle, return the close value from now until we support candle panels
        let target: Float32Array;
        if ("values" in i.data) {
          target = i.data.values;
        } else {
          return [{ value: 0, offset: 0 }];
        }

        // Check if we're outside the actual time boundaries of the data
        const firstTime = i.data.times[i.data.times.length - 1]; // oldest timestamp (array is descending)
        const lastTime = i.data.times[0]; // newest timestamp (array is descending)

        if (timeInSeconds < firstTime || timeInSeconds > lastTime) {
          // Don't render anything outside actual time boundaries
          return [];
        }

        // If we're within time boundaries but index is out of bounds, still don't render
        if (index < 0 || index >= target.length) {
          return [];
        }

        const value = target[index];

        // NaN = no data for this candle (sparse indicator gap between positions).
        // linewithbreaks plottype handles NaN natively, but return [] is a
        // safety net that works regardless of plottype (e.g., old backtests).
        if (Number.isNaN(value)) {
          return [];
        }

        return [
          {
            value,
            offset: 0,
          },
        ];
      };
    },
  };
};
